import { z } from 'zod';
import { criarCustoAquisicaoSchema } from './custos-aquisicao.js';
import { marcarEscolhidaSchema, novaVersaoSchema } from './fichas-tecnicas.js';
import { cuid } from './primitives.js';

/**
 * Impacto em cascata (Sessao 49, F): o que muda ANTES de gravar (nova aquisicao, nova versao escolhida de ficha, troca da ficha
 * escolhida). A pre-visualizacao nao grava nada (simula a gravacao real numa transacao descartada); confirmar = chamar a rota
 * de gravacao de sempre; cancelar = nao chamar nada.
 */
export const IMPACTO_MAX_LINHAS = 50;

/** POST /impacto-cascata/aquisicao: o mesmo corpo de POST /custos-aquisicao. Permissao: custoAquisicao.editar. */
export const previaAquisicaoSchema = criarCustoAquisicaoSchema;
export type PreviaAquisicaoDto = z.infer<typeof previaAquisicaoSchema>;

/** POST /impacto-cascata/ficha-nova-versao: cria a proxima versao de `fichaId` (corpo de POST /fichas-tecnicas/:id/nova-versao) E a escolhe. Permissao: ficha.editar. */
export const previaNovaVersaoSchema = novaVersaoSchema.extend({ fichaId: cuid, motivo: z.string().trim().min(1).max(200).optional() });
export type PreviaNovaVersaoDto = z.infer<typeof previaNovaVersaoSchema>;

/** POST /impacto-cascata/ficha-escolhida: o corpo de PATCH /fichas-tecnicas/:id/escolhida mais `fichaId`. Permissao: ficha.editar. */
export const previaEscolhidaSchema = marcarEscolhidaSchema.extend({ fichaId: cuid });
export type PreviaEscolhidaDto = z.infer<typeof previaEscolhidaSchema>;

export interface FichaAfetadaImpacto {
  fichaId: string;
  itemId: string;
  itemCodigo: string;
  itemDescricao: string;
  versao: number;
  /** 0 = a propria ficha que muda (troca/nova versao); 1 = usa direto o item alterado; 2+ = pela cascata. */
  nivel: number;
  /** Custo unitario da ficha escolhida (R$/un). So com custo.ver; `null` = a ficha nao calculava (antes) ou nao calcula (depois). */
  custoAntes?: string | null;
  custoDepois?: string | null;
}

/** Resumo compacto do impacto: e tambem o campo `impacto` do relatorio das execucoes de importacao. */
export interface ImpactoCascataResumo {
  /** Fichas ESCOLHIDAS cujo custo muda, ate IMPACTO_MAX_LINHAS (por nivel, depois codigo do item). */
  fichasAfetadas: FichaAfetadaImpacto[];
  /** Total de fichas escolhidas cujo custo muda (>= fichasAfetadas.length). */
  totalFichasAfetadas: number;
  /** totalFichasAfetadas - fichasAfetadas.length ("e mais N"). */
  maisN: number;
  /** Itens (distintos) que ficam com a marca "custo atualizado - revisar preco" (nova ou renovada). */
  itensRevisarPreco: number;
  /** Chaves item x tabela x condicao marcadas. */
  marcasNovas: number;
  /** true se a lista foi cortada (mais de 50) ou a cascata passou do limite de niveis. */
  truncado: boolean;
}

export type ImpactoCascataResponse = ImpactoCascataResumo;
